/**
 * Opening scene prompt template.
 *
 * Generates a Skyrim-style in media res opening from world setting and character.
 * 2nd person present tense, 150-300 words, ends on a choice-demanding moment.
 * The character starts mid-action in a vivid location with NPCs and immediate goals.
 */

/** Build the opening scene prompt. */
export function openingScenePrompt(
  worldSetting: string,
  characterName: string,
  background: string,
  genre: string,
  bestSkills?: string[],
  attributes?: { st: number; dx: number; iq: number; ht: number },
): string {
  const skillsSection =
    bestSkills && bestSkills.length > 0
      ? [
          "",
          "### Character Capabilities",
          `- **Best skills**: ${bestSkills.join(", ")}`,
          attributes
            ? `- **Attributes**: ST ${attributes.st}, DX ${attributes.dx}, IQ ${attributes.iq}, HT ${attributes.ht}`
            : "- **Attributes**: Average (around 10 in all stats — a beginning adventurer)",
          "- This is a LEVEL 1 character. They are competent but not heroic.",
          "- The opening situation MUST be solvable using at least one of the character's best skills.",
        ].join("\n")
      : [
          "",
          "### Character Capabilities",
          "- This is a LEVEL 1 character with average stats (around 10).",
          "- They are competent but not heroic. The opening should be survivable.",
        ].join("\n");

  return [
    "Generate an opening scene for a dark roguelike RPG.",
    "",
    "### World Context",
    `- **Setting**: ${worldSetting}`,
    `- **Genre**: ${genre}`,
    `- **Character**: ${characterName}`,
    `- **Background**: ${background}`,
    skillsSection,
    "",
    "### Scene Requirements (In Media Res Opening)",
    "The scene begins IN THE MIDDLE OF SOMETHING HAPPENING. The character is already",
    "active, aware, and engaged with the world. Pick ONE of these opening patterns:",
    "",
    "- **Arriving somewhere**: The character reaches a settlement, ruin, or landmark",
    "  after a journey. They see activity, hear voices, smell food or smoke.",
    "- **Mid-conversation**: The character is already talking to someone — a contact,",
    "  a stranger, a guard — who presents a problem or opportunity.",
    "- **Witnessing an event**: The character stumbles upon something in progress —",
    "  a fight, a ritual, a deal gone wrong, a creature feeding.",
    "- **Completing a task**: The character is finishing a job (delivering cargo,",
    "  scouting a location, meeting a contact) when something unexpected happens.",
    "",
    "### Mandatory Elements",
    "1. **A vivid, specific location** — not generic. Give it texture: a rain-slicked",
    "   dock, a market square with a broken fountain, a forest clearing with standing",
    "   stones. Name the place if natural.",
    "2. **At least one named NPC** — give them a name, a one-line personality trait,",
    "   and something they want from or can offer the character. The NPC should speak",
    "   at least one line of dialogue.",
    "3. **An immediate goal or problem** — the character needs to DO something right",
    "   now: make a decision, help someone, avoid danger, seize an opportunity.",
    "4. **Background connection** — the location or situation should make sense for",
    "   the character's background (a warrior near a conflict, a thief near a market,",
    "   a scholar near ruins or a library).",
    "",
    "### ABSOLUTE PROHIBITIONS",
    "- NEVER start with the character waking up, regaining consciousness, or coming to",
    "- NEVER start in a dark featureless room, cell, cave, or dungeon",
    "- NEVER start with amnesia, confusion, or the character not knowing where they are",
    '- NEVER open with "You open your eyes" or any variation of awakening',
    "- NEVER start in isolation — there must be other people or creatures present",
    "",
    "### Voice",
    "- DOS-era text adventure narrator: terse, atmospheric, gritty",
    "- Short, punchy sentences. No purple prose. No modern slang.",
    "- Dark humor where appropriate",
    "- ASCII-safe characters only",
    "",
    "### Format",
    "- LENGTH: 150-300 words. 3-5 paragraphs.",
    "- Do not include any meta-text, options, or game mechanics.",
    "- Just the narrative prose.",
    "- End on a moment that DEMANDS a player decision.",
    "",
    "### Example of a GOOD Opening Scene",
    "",
    "The rain has not let up since Thornfield. Three days of grey sky and mud,",
    "and now the gates of Ashwick rise before you — iron-banded oak, one hinge",
    "rusted through, the left door sagging like a drunk against the wall.",
    "",
    "A woman in a stained leather apron blocks your path. She has the build of",
    'someone who hauls carcasses for a living. "You the one Aldric sent?" She',
    'does not wait for your answer. "Good. Name\'s Bessa. The butcher."',
    "",
    "She jerks her thumb toward the market square behind her. Three stalls",
    "stand empty. The fourth is surrounded by a knot of townsfolk, voices",
    "rising. Someone shoves someone else. A dog barks. \"That lot's been at",
    'it since morning," Bessa says. "Merchant came in with a sealed crate.',
    "Won't open it, won't say what's inside. Half the town thinks it's",
    "medicine. Other half thinks it's cursed. Either way, they're about to",
    'crack it open themselves."',
    "",
    'She looks you up and down. "Aldric said you were good at sorting things',
    'out. So sort."',
  ].join("\n");
}
